import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid } from 'recharts';
import { TrendingUp, Users, Percent, Flame, Target, BarChart3, PieChart as PieIcon, Activity, ArrowUpRight, Zap, Snowflake, Check, Mail, XCircle } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '@/lib/api';

const heading = { fontFamily: "'Outfit', sans-serif", color: '#1D1D1F' };
const COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#6B7280', '#EF4444', '#EC4899', '#14B8A6'];

export default function LeadAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leads/analytics').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96 text-[#86868B]">Loading analytics...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="flex items-center justify-center h-96 text-[#86868B]">Failed to load analytics</div></DashboardLayout>;

  const { summary, funnel, industries, scores, grades, sources, engagement, nurture, trend } = data;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="lead-analytics-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#1D1D1F]" style={heading}>Lead Analytics</h1>
          <p className="text-[13px] text-[#86868B] mt-1">Pipeline metrics, scoring insights, and conversion tracking</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Leads', val: summary.total_leads, icon: Users, color: 'from-blue-500 to-indigo-600' },
            { label: 'Conversion', val: `${summary.conversion_rate}%`, icon: Percent, color: 'from-emerald-500 to-teal-600' },
            { label: 'Avg Score', val: summary.avg_score, icon: TrendingUp, color: 'from-violet-500 to-purple-600' },
            { label: 'Engaged', val: `${summary.engaged_pct}%`, icon: Target, color: 'from-orange-500 to-amber-500' },
            { label: 'Demo Completed', val: summary.demo_completed, icon: Check, color: 'from-pink-500 to-rose-600' },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm" data-testid={`analytics-stat-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center`}>
                  <s.icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[10px] font-semibold text-[#86868B] uppercase tracking-wider">{s.label}</span>
              </div>
              <p className="text-2xl font-bold text-[#1D1D1F]" style={heading}>{s.val}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pipeline Funnel */}
          <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="pipeline-funnel">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="w-5 h-5 text-[#86868B]" />
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Pipeline Funnel</h2>
            </div>
            <div className="space-y-3">
              {funnel.map((stage, i) => (
                <div key={stage.stage} className="flex items-center gap-3">
                  <span className="text-[12px] font-medium text-[#86868B] w-20 text-right">{stage.stage}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(stage.pct, 2)}%` }}
                      transition={{ delay: 0.2 + i * 0.1, duration: 0.6 }}
                      className="h-full rounded-full flex items-center justify-end pr-3"
                      style={{ backgroundColor: stage.color }}>
                      {stage.pct > 10 && <span className="text-white text-[11px] font-bold">{stage.count}</span>}
                    </motion.div>
                    {stage.pct <= 10 && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#1D1D1F]">{stage.count}</span>}
                  </div>
                  <span className="text-[11px] font-semibold text-[#86868B] w-12">{stage.pct}%</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Leads Over Time */}
          <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            data-testid="leads-trend">
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-5 h-5 text-[#86868B]" />
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Leads Over Time (30 days)</h2>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tickFormatter={v => v.slice(5)} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Area type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2} fill="url(#leadGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Industry Breakdown */}
          <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            data-testid="industry-breakdown">
            <div className="flex items-center gap-2 mb-5">
              <PieIcon className="w-5 h-5 text-[#86868B]" />
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>By Industry</h2>
            </div>
            <div className="space-y-2">
              {industries.slice(0, 6).map((ind, i) => (
                <div key={ind.name} className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-[12px] text-[#1D1D1F] flex-1 truncate">{ind.name}</span>
                  <span className="text-[12px] font-bold text-[#1D1D1F]">{ind.count}</span>
                  <span className="text-[10px] text-[#86868B]">{ind.pct}%</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Score Distribution */}
          <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            data-testid="score-distribution">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="w-5 h-5 text-[#86868B]" />
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Score Distribution</h2>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={scores}>
                <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Grade & Nurture */}
          <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            data-testid="grade-nurture">
            <div className="flex items-center gap-2 mb-5">
              <Flame className="w-5 h-5 text-[#86868B]" />
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Grade & Nurture</h2>
            </div>
            {/* Grade pills */}
            <div className="flex items-center gap-2 mb-5">
              {grades.map(g => (
                <div key={g.grade} className="flex-1 text-center p-3 rounded-lg" style={{ backgroundColor: g.color + '15' }}>
                  <p className="text-xl font-bold" style={{ color: g.color, ...heading }}>{g.count}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: g.color }}>{g.grade}</p>
                </div>
              ))}
            </div>
            {/* Nurture stats */}
            <div className="border-t border-gray-100 pt-4 space-y-2">
              <p className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider mb-2">Nurture Activity</p>
              {[
                { icon: Mail, label: 'Sent', val: nurture.sent, color: 'text-success' },
                { icon: Activity, label: 'Pending', val: nurture.pending, color: 'text-warning' },
                { icon: XCircle, label: 'Cancelled', val: nurture.cancelled, color: 'text-gray-400' },
              ].map(n => (
                <div key={n.label} className="flex items-center gap-2">
                  <n.icon className={`w-3.5 h-3.5 ${n.color}`} />
                  <span className="text-[12px] text-[#86868B] flex-1">{n.label}</span>
                  <span className={`text-[13px] font-bold ${n.color}`}>{n.val}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
